import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, withDirectives as _withDirectives, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex flex-wrap items-center gap-x-6 gap-y-3" }
const _hoisted_2 = { class: "flex min-w-0 flex-1 items-center gap-3" }
const _hoisted_3 = { class: "shrink-0 font-semibold" }
const _hoisted_4 = {
  key: 1,
  class: "text-sm text-gray-400"
}
const _hoisted_5 = {
  key: 0,
  class: "rounded-lg border border-dashed border-gray-200 bg-white p-10 text-center text-sm text-gray-400"
}
const _hoisted_6 = {
  key: 1,
  class: "flex flex-col items-center justify-center gap-3 rounded-lg border border-gray-200 bg-white p-16"
}
const _hoisted_7 = { class: "text-sm text-gray-500" }
const _hoisted_8 = {
  key: 2,
  class: "rounded-lg border border-gray-200 bg-white p-10 text-center text-sm text-gray-400"
}
const _hoisted_9 = { class: "rounded-lg border border-gray-200 bg-white p-4" }
const _hoisted_10 = { class: "mb-3 text-sm font-semibold text-gray-700" }
const _hoisted_11 = { class: "flex flex-wrap divide-x divide-gray-200" }
const _hoisted_12 = { class: "flex items-center gap-x-3 rounded-lg border border-gray-200 bg-white p-3" }
const _hoisted_13 = ["disabled", "title"]
const _hoisted_14 = ["value"]
const _hoisted_15 = ["disabled", "title"]
const _hoisted_16 = { class: "grid grid-cols-2 gap-4 sm:grid-cols-4" }
const _hoisted_17 = { class: "rounded-lg border border-gray-200 bg-white p-4" }
const _hoisted_18 = { class: "mb-3 text-sm font-semibold text-gray-700" }
const _hoisted_19 = { class: "overflow-x-auto" }
const _hoisted_20 = { class: "min-w-full divide-y divide-gray-200 text-sm" }
const _hoisted_21 = { class: "bg-gray-50 text-xs font-semibold text-gray-500" }
const _hoisted_22 = { class: "px-3 py-2 text-left" }
const _hoisted_23 = { class: "px-3 py-2 text-left" }
const _hoisted_24 = { class: "px-3 py-2 text-right" }
const _hoisted_25 = { class: "px-3 py-2 text-right" }
const _hoisted_26 = {
  key: 0,
  class: "px-3 py-2 text-right"
}
const _hoisted_27 = { class: "px-3 py-2 text-right" }
const _hoisted_28 = { class: "px-3 py-2 text-right" }
const _hoisted_29 = { class: "divide-y divide-gray-100" }
const _hoisted_30 = { class: "px-3 py-2 tabular-nums" }
const _hoisted_31 = { class: "px-3 py-2 tabular-nums" }
const _hoisted_32 = { class: "px-3 py-2 text-right tabular-nums" }
const _hoisted_33 = { class: "px-3 py-2 text-right tabular-nums" }
const _hoisted_34 = {
  key: 0,
  class: "px-3 py-2 text-right tabular-nums"
}
const _hoisted_35 = { class: "px-3 py-2 text-right tabular-nums" }
const _hoisted_36 = {
  key: 1,
  class: "px-3 py-2 text-right tabular-nums"
}
const _hoisted_37 = {
  key: 2,
  class: "px-3 py-2 text-right tabular-nums"
}

import { computed, ref, watch } from 'vue';
import { addDays, parseISO } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-vue-next';
import DashboardContentLayout from '@/components/layouts/dashboardLayout/content/DashboardContentLayout.vue';
import Spinner from '@/components/ui/Spinner.vue';
import AccumulatedStat from './AccumulatedStat.vue';
import WeekKpi from './WeekKpi.vue';
import FarmButtonPicker from './FarmButtonPicker.vue';
import LifeCycleChart from './LifeCycleChart.vue';
import { store } from '@/plugins/store';
import { MutationType } from '@/plugins/store/mutations';
import { useWeekOverviewData } from '@/plugins/store/actions/queries/week-overview';
import { i18n } from '@/plugins/internationalization/i18n';
import { PigType } from '@/types';
import type { WeekDataDetails } from '@/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'Index',
  setup(__props) {

const isWeaners = computed(() => store.state.pigType === PigType.Weaners);

// Farms in the global scope with at least one barn of the active pig type - same "valid farms"
// definition used by /monitoring/section-status's farm picker.
const validFarms = computed(() =>
    store.state.farms.filter(
        (farm) => store.state.selectedFarmIds.includes(farm.farm_id) && store.state.barns.some((barn) => barn.farm_id === farm.farm_id && barn.is_weaners === isWeaners.value)
    )
);

// Kept in the store so it survives navigating away and back, but only honoured while the pig type
// and period it was chosen under are unchanged. A directory switch reloads the app, clearing it.
const selectedFarmId = computed<number | null>({
    get() {
        const saved = store.state.weekOverviewFarm;
        if (
            saved &&
            saved.pigType === store.state.pigType &&
            saved.period === store.state.periodPickerDateString &&
            validFarms.value.some((farm) => farm.farm_id === saved.farmId)
        ) {
            return saved.farmId;
        }
        return null;
    },
    set(farmId) {
        store.commit(MutationType.SetWeekOverviewFarm, farmId === null ? null : { farmId, pigType: store.state.pigType, period: store.state.periodPickerDateString });
    },
});

// Drop the selection if it falls out of scope (global farm selection or pig-type toggle changed);
// never auto-selects a farm - the page starts with none chosen.
watch(validFarms, (farms) => {
    if (selectedFarmId.value !== null && !farms.some((farm) => farm.farm_id === selectedFarmId.value)) {
        selectedFarmId.value = null;
    }
});

const { data: weekOverviewResponse, isLoading } = useWeekOverviewData(selectedFarmId, isWeaners);
const weekData = computed(() => weekOverviewResponse.value?.data ?? []);
const kpiIcons = computed(() => weekOverviewResponse.value?.icons);

// Client-side filter by the period picker's selected range, applied to each batch's insertion
// date only - a batch's own life-cycle data (Details) is never clipped at the range's end date.
// Analytics-v2 already returns the farm's whole history in one call; there is no
// date-range parameter to the API for this reason.
const inRangeBatches = computed(() => {
    const periodParts = store.state.periodPickerDateString.split(' - ');
    if (periodParts.length !== 2) return [];
    const from = new Date(new Date(Date.parse(periodParts[0])).setHours(0, 0, 0, 0));
    const to = new Date(new Date(Date.parse(periodParts[1])).setHours(23, 59, 59, 999));

    return weekData.value
        .filter((batch) => {
            const insertionDate = new Date(batch.firstInsertionDate);
            return insertionDate >= from && insertionDate <= to;
        })
        .sort((a, b) => a.mondayOfWeek.localeCompare(b.mondayOfWeek));
});

// Defaults to the most recent batch in range; resets whenever the in-range list itself changes
// (farm/period/pig-type switch), not on every recompute of the same list.
const selectedBatchIndex = ref(0);
// Immediate: a farm restored from the store can already have cached data when this runs.
watch(
    inRangeBatches,
    (batches) => {
        selectedBatchIndex.value = batches.length ? batches.length - 1 : 0;
    },
    { immediate: true }
);

const selectedBatch = computed(() => inRangeBatches.value[selectedBatchIndex.value] ?? null);

const selectedBatchRemaining = computed(() => {
    const details = selectedBatch.value?.details ?? [];
    return details.length ? details[details.length - 1].remaining : selectedBatch.value?.totalInserted ?? 0;
});

// Weaners leave by sale or transfer rather than slaughter; the odd small-slaughter exit
// is folded in so the weaner columns still add up with "remaining".
function soldAndTransferred(detail: WeekDataDetails): number {
    return detail.departed + detail.transferred + (isWeaners.value ? detail.slaughtered : 0);
}

// Animals that left alive this week - the population avgExitWeight is averaged over.
function exitCount(detail: WeekDataDetails): number {
    return detail.departed + detail.transferred + detail.slaughtered;
}

const selectedBatchSoldAndTransferred = computed(() => (selectedBatch.value?.details ?? []).reduce((sum, detail) => sum + soldAndTransferred(detail), 0));

const selectedBatchMortalityPercent = computed(() => {
    const batch = selectedBatch.value;
    if (!batch || batch.totalInserted === 0) return 0;
    return (batch.totalDead / batch.totalInserted) * 100;
});

// Total live-weight gain per animal (kg) = daily gain (g/day) x production days / 1000.
const selectedBatchAvgGainKg = computed(() => {
    const batch = selectedBatch.value;
    if (!batch) return 0;
    return (batch.dailyGain * batch.productionDays) / 1000;
});

const accumulated = computed(() => {
    const batches = inRangeBatches.value;
    const totalInserted = batches.reduce((sum, batch) => sum + batch.totalInserted, 0);
    const totalSlaughtered = batches.reduce((sum, batch) => sum + batch.totalSlaughtered, 0);
    const totalDead = batches.reduce((sum, batch) => sum + batch.totalDead, 0);
    const totalSoldAndTransferred = batches.reduce((sum, batch) => sum + batch.totalDeparted + batch.totalTransferred + (isWeaners.value ? batch.totalSlaughtered : 0), 0);

    // Weighted by animal count: a weighted average of a per-batch rate, weighted by
    // its own denominator, reduces to sum-of-numerators / sum-of-denominators.
    const averageDeadPercent = totalInserted > 0 ? (totalDead / totalInserted) * 100 : 0;

    const allDetails = batches.flatMap((batch) => batch.details);
    // Slaughter pigs: slaughter weight over slaughtered animals. Weaners carry no slaughter weight - their
    // exit weight is the live weight of everything that left alive.
    const exitWeightTotal = allDetails.reduce(
        (sum, detail) => sum + (isWeaners.value ? (detail.avgExitWeight ?? 0) * exitCount(detail) : (detail.avgSlaughterWeight ?? 0) * detail.slaughtered),
        0
    );
    const exitCountTotal = allDetails.reduce((sum, detail) => sum + (isWeaners.value ? exitCount(detail) : detail.slaughtered), 0);
    const averageExitWeight = exitCountTotal > 0 ? exitWeightTotal / exitCountTotal : 0;

    return {
        numberOfBatches: batches.length,
        totalInserted,
        totalSlaughtered,
        totalDead,
        totalSoldAndTransferred,
        averageDeadPercent,
        averageExitWeight,
    };
});

// parseISO reads date-only strings as local midnight, so the formatted day is the same in every timezone.
function formatDate(date: string | Date): string {
    return i18n.global.d(typeof date === 'string' ? parseISO(date) : date, 'middle');
}

// A cohort is an insertion week starting on Monday; its last day is the Sunday.
function sundayOf(mondayOfWeek: string): Date {
    return addDays(parseISO(mondayOfWeek), 6);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(DashboardContentLayout, {
    class: "space-y-6",
    heading: _ctx.$t('views.weekOverview.header.heading'),
    "has-pig-type-picker": "",
    "has-period-picker": ""
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("span", _hoisted_3, _toDisplayString(_ctx.$t('views.weekOverview.farmPicker.label')) + ":", 1),
          (validFarms.value.length)
            ? (_openBlock(), _createBlock(FarmButtonPicker, {
                key: 0,
                farms: validFarms.value,
                modelValue: selectedFarmId.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedFarmId).value = $event))
              }, null, 8, ["farms", "modelValue"]))
            : (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(_ctx.$t('views.weekOverview.farmPicker.noFarms')), 1))
        ])
      ]),
      (selectedFarmId.value === null)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(_ctx.$t('views.weekOverview.texts.selectFarm')), 1))
        : (_unref(isLoading))
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createVNode(Spinner, { class: "h-10 w-10" }),
              _createElementVNode("span", _hoisted_7, _toDisplayString(_ctx.$t('views.weekOverview.texts.loading')), 1)
            ]))
          : (!inRangeBatches.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(_ctx.$t('views.weekOverview.texts.noData')), 1))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("div", _hoisted_10, _toDisplayString(_ctx.$t('views.weekOverview.accumulated.title')), 1),
                  _createElementVNode("div", _hoisted_11, [
                    _createVNode(AccumulatedStat, {
                      label: _ctx.$t('views.weekOverview.accumulated.numberOfBatches'),
                      value: accumulated.value.numberOfBatches
                    }, null, 8, ["label", "value"]),
                    _createVNode(AccumulatedStat, {
                      label: _ctx.$t('views.weekOverview.accumulated.totalInserted'),
                      value: accumulated.value.totalInserted
                    }, null, 8, ["label", "value"]),
                    (!isWeaners.value)
                      ? (_openBlock(), _createBlock(AccumulatedStat, {
                          key: 0,
                          label: _ctx.$t('views.weekOverview.accumulated.totalSlaughtered'),
                          value: accumulated.value.totalSlaughtered
                        }, null, 8, ["label", "value"]))
                      : _createCommentVNode("", true),
                    _createVNode(AccumulatedStat, {
                      label: _ctx.$t('views.weekOverview.accumulated.totalDead'),
                      value: accumulated.value.totalDead
                    }, null, 8, ["label", "value"]),
                    _createVNode(AccumulatedStat, {
                      label: _ctx.$t('views.weekOverview.accumulated.totalSoldAndTransferred'),
                      value: accumulated.value.totalSoldAndTransferred
                    }, null, 8, ["label", "value"]),
                    _createVNode(AccumulatedStat, {
                      label: _ctx.$t('views.weekOverview.accumulated.averageDead'),
                      value: accumulated.value.averageDeadPercent,
                      suffix: "%"
                    }, null, 8, ["label", "value"]),
                    _createVNode(AccumulatedStat, {
                      label: _ctx.$t(isWeaners.value ? 'views.weekOverview.accumulated.averageLiveExitWeight' : 'views.weekOverview.accumulated.averageExitWeight'),
                      value: accumulated.value.averageExitWeight,
                      suffix: "kg"
                    }, null, 8, ["label", "value"])
                  ])
                ]),
                _createElementVNode("div", _hoisted_12, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "rounded p-1.5 hover:bg-gray-100 disabled:opacity-40 transition-colors",
                    disabled: selectedBatchIndex.value <= 0,
                    title: _ctx.$t('views.weekOverview.batchSelector.previous'),
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (selectedBatchIndex.value -= 1))
                  }, [
                    _createVNode(_unref(ChevronLeft), {
                      class: "h-4 w-4",
                      "aria-hidden": "true"
                    })
                  ], 8, _hoisted_13),
                  _withDirectives(_createElementVNode("select", {
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedBatchIndex).value = $event)),
                    class: "flex-1 rounded-md border border-gray-200 py-1.5 text-sm"
                  }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(inRangeBatches.value, (batch, index) => {
                      return (_openBlock(), _createElementBlock("option", {
                        key: batch.mondayOfWeek,
                        value: index
                      }, _toDisplayString(_ctx.$t('views.weekOverview.batchSelector.label')) + " - " + _toDisplayString(formatDate(batch.mondayOfWeek)) + " – " + _toDisplayString(formatDate(sundayOf(batch.mondayOfWeek))), 9, _hoisted_14))
                    }), 128))
                  ], 512), [
                    [
                      _vModelSelect,
                      selectedBatchIndex.value,
                      void 0,
                      { number: true }
                    ]
                  ]),
                  _createElementVNode("button", {
                    type: "button",
                    class: "rounded p-1.5 hover:bg-gray-100 disabled:opacity-40 transition-colors",
                    disabled: selectedBatchIndex.value >= inRangeBatches.value.length - 1,
                    title: _ctx.$t('views.weekOverview.batchSelector.next'),
                    onClick: _cache[3] || (_cache[3] = ($event: any) => (selectedBatchIndex.value += 1))
                  }, [
                    _createVNode(_unref(ChevronRight), {
                      class: "h-4 w-4",
                      "aria-hidden": "true"
                    })
                  ], 8, _hoisted_15)
                ]),
                (selectedBatch.value)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createElementVNode("div", _hoisted_16, [
                        _createVNode(WeekKpi, {
                          label: _ctx.$t('views.weekOverview.summary.totalInserted'),
                          value: selectedBatch.value.totalInserted,
                          suffix: _ctx.$t('views.weekOverview.texts.pcsUnit'),
                          "icon-id": kpiIcons.value?.totalInsertedIconId
                        }, null, 8, ["label", "value", "suffix", "icon-id"]),
                        _createVNode(WeekKpi, {
                          label: isWeaners.value ? _ctx.$t('views.weekOverview.summary.totalSoldAndTransferred') : _ctx.$t('views.weekOverview.summary.totalSlaughtered'),
                          value: isWeaners.value ? selectedBatchSoldAndTransferred.value : selectedBatch.value.totalSlaughtered,
                          suffix: _ctx.$t('views.weekOverview.texts.pcsUnit'),
                          "icon-id": kpiIcons.value?.totalSlaughteredIconId
                        }, null, 8, ["label", "value", "suffix", "icon-id"]),
                        _createVNode(WeekKpi, {
                          label: _ctx.$t('views.weekOverview.summary.totalDead'),
                          value: selectedBatch.value.totalDead,
                          suffix: _ctx.$t('views.weekOverview.texts.pcsUnit'),
                          "icon-id": kpiIcons.value?.totalDeadIconId
                        }, null, 8, ["label", "value", "suffix", "icon-id"]),
                        _createVNode(WeekKpi, {
                          label: _ctx.$t('views.weekOverview.summary.remaining'),
                          value: selectedBatchRemaining.value,
                          suffix: _ctx.$t('views.weekOverview.texts.pcsUnit'),
                          "icon-id": kpiIcons.value?.remainingIconId
                        }, null, 8, ["label", "value", "suffix", "icon-id"]),
                        _createVNode(WeekKpi, {
                          label: _ctx.$t('views.weekOverview.summary.avgGain'),
                          value: selectedBatchAvgGainKg.value,
                          suffix: "kg",
                          "icon-id": kpiIcons.value?.avgGainIconId
                        }, null, 8, ["label", "value", "icon-id"]),
                        _createVNode(WeekKpi, {
                          label: _ctx.$t('views.weekOverview.summary.avgDailyGain'),
                          value: selectedBatch.value.dailyGain,
                          suffix: "g",
                          "icon-id": kpiIcons.value?.dailyGainIconId
                        }, null, 8, ["label", "value", "icon-id"]),
                        _createVNode(WeekKpi, {
                          label: _ctx.$t('views.weekOverview.summary.avgMortality'),
                          value: selectedBatchMortalityPercent.value,
                          suffix: "%",
                          "icon-id": kpiIcons.value?.mortalityIconId
                        }, null, 8, ["label", "value", "icon-id"]),
                        _createVNode(WeekKpi, {
                          label: _ctx.$t('views.weekOverview.summary.avgProductionDays'),
                          value: selectedBatch.value.productionDays,
                          "fixed-decimals": 1,
                          suffix: _ctx.$t('views.weekOverview.texts.daysUnit'),
                          "icon-id": kpiIcons.value?.productionDaysIconId
                        }, null, 8, ["label", "value", "suffix", "icon-id"])
                      ]),
                      _createVNode(LifeCycleChart, {
                        details: selectedBatch.value.details,
                        "inserted-date": `${formatDate(selectedBatch.value.firstInsertionDate)} – ${formatDate(sundayOf(selectedBatch.value.mondayOfWeek))}`,
                        "inserted-count": selectedBatch.value.totalInserted,
                        "avg-weight": selectedBatch.value.avgWeight,
                        "is-weaners": isWeaners.value
                      }, null, 8, ["details", "inserted-date", "inserted-count", "avg-weight", "is-weaners"]),
                      _createElementVNode("div", _hoisted_17, [
                        _createElementVNode("div", _hoisted_18, _toDisplayString(_ctx.$t('views.weekOverview.lifeCycle.tableTitle')), 1),
                        _createElementVNode("div", _hoisted_19, [
                          _createElementVNode("table", _hoisted_20, [
                            _createElementVNode("thead", _hoisted_21, [
                              _createElementVNode("tr", null, [
                                _createElementVNode("th", _hoisted_22, _toDisplayString(_ctx.$t('views.weekOverview.lifeCycle.tableHeadings.week')), 1),
                                _createElementVNode("th", _hoisted_23, _toDisplayString(_ctx.$t('views.weekOverview.lifeCycle.tableHeadings.date')), 1),
                                _createElementVNode("th", _hoisted_24, _toDisplayString(_ctx.$t('views.weekOverview.lifeCycle.tableHeadings.dead')), 1),
                                _createElementVNode("th", _hoisted_25, _toDisplayString(_ctx.$t('views.weekOverview.lifeCycle.tableHeadings.soldTransferred')), 1),
                                (!isWeaners.value)
                                  ? (_openBlock(), _createElementBlock("th", _hoisted_26, _toDisplayString(_ctx.$t('views.weekOverview.lifeCycle.tableHeadings.slaughtered')), 1))
                                  : _createCommentVNode("", true),
                                _createElementVNode("th", _hoisted_27, _toDisplayString(_ctx.$t('views.weekOverview.lifeCycle.tableHeadings.remaining')), 1),
                                _createElementVNode("th", _hoisted_28, _toDisplayString(_ctx.$t(isWeaners.value ? 'views.weekOverview.lifeCycle.tableHeadings.avgExitWeight' : 'views.weekOverview.lifeCycle.tableHeadings.avgSlaughterWeight')), 1)
                              ])
                            ]),
                            _createElementVNode("tbody", _hoisted_29, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedBatch.value.details, (detail, index) => {
                                return (_openBlock(), _createElementBlock("tr", {
                                  key: detail.mondayOfDate
                                }, [
                                  _createElementVNode("td", _hoisted_30, _toDisplayString(index), 1),
                                  _createElementVNode("td", _hoisted_31, _toDisplayString(formatDate(detail.mondayOfDate)), 1),
                                  _createElementVNode("td", _hoisted_32, _toDisplayString(_ctx.$n(detail.dead ?? 0)), 1),
                                  _createElementVNode("td", _hoisted_33, _toDisplayString(_ctx.$n(soldAndTransferred(detail) || 0)), 1),
                                  (!isWeaners.value)
                                    ? (_openBlock(), _createElementBlock("td", _hoisted_34, _toDisplayString(_ctx.$n(detail.slaughtered ?? 0)), 1))
                                    : _createCommentVNode("", true),
                                  _createElementVNode("td", _hoisted_35, _toDisplayString(_ctx.$n(detail.remaining ?? 0)), 1),
                                  (isWeaners.value)
                                    ? (_openBlock(), _createElementBlock("td", _hoisted_36, _toDisplayString(exitCount(detail) > 0 ? _ctx.$n(detail.avgExitWeight ?? 0, 'decimal') : '—'), 1))
                                    : (_openBlock(), _createElementBlock("td", _hoisted_37, _toDisplayString(detail.slaughtered > 0 ? _ctx.$n(detail.avgSlaughterWeight ?? 0, 'decimal') : '—'), 1))
                                ]))
                              }), 128))
                            ])
                          ])
                        ])
                      ])
                    ], 64))
                  : _createCommentVNode("", true)
              ], 64))
    ]),
    _: 1
  }, 8, ["heading"]))
}
}

})